import React from 'react';
import { HoodPageProps } from 'screens/AreaPage/types';
import { ScrollSpyProvider, ScrollSpyTarget } from 'components/scroll-spy';
import { StatsSection } from 'screens/AreaPage/sections/Stats';
import { SchoolsSection } from 'screens/AreaPage/sections/Schools';
import { SocialRankingSection } from 'screens/AreaPage/sections/SocialRanking';
import ListingsSection from 'components/listings-section';
import NearbyProjectsSection from 'components/nearby-projects-section';
import { WithContentSlotSidePaddingContainer, WithContentSlotBottomPadding, WithContentSlotTopPadding } from 'ds/components/Layout';
import { Section as SectionEnum, Section } from 'components/navigation/subheader/SectionsNavigation';
import Footer from 'components/footer';
import { OverviewSection } from 'screens/AreaPage/sections/Overview';
import Metrics from 'screens/AreaPage/sections/Metrics';
import { useLocale } from 'locale';
import DetailsSection from 'components/details-section/DetailsSection';
import Trends from 'components/widget/trends';
import Listings from 'components/widget/prices/section/listings';
import { PricesBorder } from 'components/widget/prices/section/styled';
import { H3 } from 'ds/components/typography';
import { SectionHeader, TrendsInnerTitle, LocalWinnersSection, TrendsChartTitle, BlogSectionBg, BlogWrap } from 'screens/AreaPage/styled';
import AreaHighlights from 'components/area-highlights/AreaHighlights';
import { ExpandedInsightsSummary } from 'components/expanded-insights-summary';
import { ScrollRestorer } from 'components/scroll-restorer';
import { LocalWinners } from 'components/madad/local-winners/LocalWinners';
import { isArrayEmpty } from 'utils/arrayUtils';
import ProjectAdBanner from 'components/ads/ProjectAdBanner';
import ProjectAdBannerProvider from 'components/ads/ProjectAdBannerSource';
import { AdGetter } from 'components/ads/types';
import BlogSection from 'components/blog-carousel/area-page';
import LeadingAgentsCarousel from 'components/leading-agent-carousel';


export const HoodPage: React.FC<HoodPageProps> = (props) => {
  const {
    localData,
    setVisibleSection,
    activeInsightId,
    searchArea,
    pricesItemsCount,
    madadAgencies,
    articles,
    leadingAgents,
    schoolsData,
  } = props;

  const { t } = useLocale();

  if (!localData || !searchArea) return null;

  const showLeadingAgents = leadingAgents && leadingAgents.length;
  const showMadadLocalWinnersSection = !isArrayEmpty(madadAgencies);

  const {
    city,
    description,
    kindergartenInArea,
    demographic,
    userRatings,
    neighbourhood,
  } = localData;

    // The demographic index is incorrect (old data) in "localDate.zoneSummary", so we use it directly. Can be removed after the BE is fixed (ILDEV-9756)
  const summary = demographic ? {
    ...localData.zoneSummary,
    demographicIndex: demographic.demographicIndex as number,
  } : localData.zoneSummary;

  let content;

  const highlights = (
    <AreaHighlights
      isWhiteBg
      withoutWrapper
      bigCarouselHeaders
      scrollSpyTargetId={SectionEnum.INSIGHTS}
    />
  );

  if (activeInsightId) {
    content = <ExpandedInsightsSummary />;
  }
  else {
    content = (
      <ScrollSpyProvider onChange={setVisibleSection}>
        <ProjectAdBannerProvider>
          {(getBanner: AdGetter) => (
            <>
              <WithContentSlotBottomPadding data-auto="madlan-nghb-page">
                <DetailsSection
                  zoneName={searchArea.neighbourhood}
                  city={city}
                  type={searchArea.type}
                  location={searchArea.location}
                  borough={searchArea.borough}
                />
                <WithContentSlotSidePaddingContainer>
                  <ScrollSpyTarget targetId={Section.OVERVIEW_AREA}>
                    <OverviewSection description={description ? description : searchArea.description} areaNeighbourhoodName={neighbourhood} />
                  </ScrollSpyTarget>
                  <Metrics summary={summary} type={searchArea.type} />

                  <ProjectAdBanner fullWidth ad={getBanner()} mTop={7} mBottom={7} />
                  <Trends
                    innerTitle={(
                      <ScrollSpyTarget targetId={Section.TRENDS}>
                        <TrendsInnerTitle weight="bold">
                          {t('localPage.prices.title2', { area: searchArea.name })}
                        </TrendsInnerTitle>
                      </ScrollSpyTarget>
                    )}
                    chartTitle={(
                      <ScrollSpyTarget targetId={Section.PRICE_TRENDS}>
                        <TrendsChartTitle />
                      </ScrollSpyTarget>
                    )}
                  />

                  <ScrollSpyTarget targetId={Section.DEALS}>
                    <SectionHeader>
                      <H3 weight="bold">{t('widget.prices.tableAreaHeader', {
                        count: pricesItemsCount,
                        area: searchArea.name,
                      })}</H3>
                    </SectionHeader>
                    <PricesBorder>
                      <Listings showTabs={false} />
                    </PricesBorder>
                  </ScrollSpyTarget>

                  {showMadadLocalWinnersSection ? (
                    <ScrollSpyTarget targetId={Section.MADAD}>
                      <LocalWinnersSection>
                        <LocalWinners
                          agents={[]}
                          agencies={madadAgencies}
                          area="hood"
                        />
                      </LocalWinnersSection>
                    </ScrollSpyTarget>
                  ) : null}
                </WithContentSlotSidePaddingContainer>
                {highlights}
                {articles && articles.length ? (
                  <BlogSectionBg>
                    <BlogWrap>
                      <BlogSection />
                    </BlogWrap>
                  </BlogSectionBg>
                ) : null}
                <WithContentSlotSidePaddingContainer>
                  <ScrollSpyTarget targetId={Section.REVIEWS}>
                    <StatsSection userRatings={userRatings} />
                  </ScrollSpyTarget>
                </WithContentSlotSidePaddingContainer>

                {showLeadingAgents ? (
                  <>
                    <LeadingAgentsCarousel items={leadingAgents} source="hood-page" />
                  </>
                 ) : <WithContentSlotTopPadding />}

                <WithContentSlotSidePaddingContainer>
                  <ScrollSpyTarget targetId={Section.SCHOOLS}>
                    <SchoolsSection
                      schoolData={schoolsData}
                      kindergartenInArea={kindergartenInArea}
                      areaNeighbourhoodName={neighbourhood}
                    />
                  </ScrollSpyTarget>
                </WithContentSlotSidePaddingContainer>
                <WithContentSlotSidePaddingContainer>
                  <ScrollSpyTarget targetId={Section.SOCIAL_INDEX}>
                    <SocialRankingSection demographic={demographic} areaNeighbourhoodName={neighbourhood} />
                  </ScrollSpyTarget>
                </WithContentSlotSidePaddingContainer>

                <WithContentSlotSidePaddingContainer>
                  <ScrollSpyTarget targetId={Section.LISTINGS}>
                    <ListingsSection
                      term={searchArea.docId}
                      zoneName={searchArea.name}
                      type={searchArea.type}
                      areaNeighbourhoodName={neighbourhood}
                    />
                  </ScrollSpyTarget>

                  <ProjectAdBanner fullWidth ad={getBanner()} mTop={7} mBottom={7} />

                  <ScrollSpyTarget targetId={Section.PROJECTS}>
                    <NearbyProjectsSection
                      term={searchArea.docId}
                      zoneName={searchArea.name}
                      type={searchArea.type}
                      areaNeighbourhoodName={neighbourhood}
                    />
                  </ScrollSpyTarget>
                </WithContentSlotSidePaddingContainer>
              </WithContentSlotBottomPadding>
              <ScrollSpyTarget targetId={Section.FOOTER}>
                <Footer />
              </ScrollSpyTarget>
            </>
          )}
        </ProjectAdBannerProvider>
      </ScrollSpyProvider>
    );
  }

  return (
    <>
      {content}
      <ScrollRestorer restore={!activeInsightId} />
    </>
  );
};
